import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from "@stencil/core";

import { StzhCardlistCollapseAllEvent } from "../../index";
import { StzhCardlistLocalizedText } from "./stzh-cardlist.localization";

/**
 * @slot - Slot for `stzh-card` elements
 * @slot description - Slot for description
 * @slot footer - Slot for footer elements
 */
@Component({
  tag: "stzh-cardlist",
  styleUrl: "stzh-cardlist.scss",
  scoped: true,
})
export class StzhCardlist {
  /** Translation strings */
  @Prop() localization: StzhCardlistLocalizedText;

  @Prop({ reflect: true }) layout: "fullwidth" | "searchresults" | "subpages" | "teasers" | "pi-teasers" = "fullwidth";

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Whether collapse/expand toggle link */
  @Prop({ reflect: true }) showToggleLink: boolean = false;

  /** Whether sorting should be enabled */
  @Prop() sortableEnabled: boolean = false;

  /** HTML attribute that is used by the `toArray()` of sortable method */
  @Prop() sortableDataIdAttribute: string = "data-id";

  /** Force maximum 3 columns layout instead of 4 */
  @Prop() max3Columns: boolean = false;

  @Element() element: HTMLStzhCardlistElement;

  /** Collapse all event */
  @Event() stzhCollapseAll: EventEmitter<StzhCardlistCollapseAllEvent>;

  @Listen("stzhCollapse")
  collapseListener() {
    this.updateCollapsedState();
  }

  @State() allCollapsed: boolean = false;
  @State() isStzhAnchornavVisible: boolean = false;

  private collapseClick = () => {
    const cards = Array.from(this.element.querySelectorAll("stzh-card"));
    cards.forEach(card => {
      if (card.collapsible) {
        card.collapsed = !this.allCollapsed;
      }
    });

    this.allCollapsed = !this.allCollapsed;

    this.stzhCollapseAll.emit({
      component: "stzh-cardlist",
      allCollapsed: this.allCollapsed,
    });
  };

  private updateCollapsedState = () => {
    const cards = Array.from(this.element.querySelectorAll("stzh-card"));
    this.allCollapsed = !cards.some(card => !card.collapsed);
  };

  private sortableGhostClone: Function = (dragElement: HTMLElement) => {
    // const [clonedElement, clonedStzhElement, originalStzhElement] = cloneWrappedStzhElement(dragElement);

    // if (
    //   clonedStzhElement
    //   && originalStzhElement
    //   && clonedStzhElement.tagName.toLowerCase() === "stzh-card"
    // ) {
    //   const clonedStzhElementCard: HTMLStzhCardElement = clonedStzhElement as HTMLStzhCardElement;
    //   const originalStzhElementCard: HTMLStzhCardElement = originalStzhElement as HTMLStzhCardElement;

    //   // copy every property that is not reflected to its attribute
    //   clonedStzhElementCard.heading = originalStzhElementCard.heading;
    //   clonedStzhElementCard.subtitle = originalStzhElementCard.subtitle;
    //   clonedStzhElementCard.headerActions = originalStzhElementCard.headerActions;
    //   clonedStzhElementCard.footerActions = originalStzhElementCard.footerActions;
    //   clonedStzhElementCard.hideMovementActions = originalStzhElementCard.hideMovementActions;
    // }

    // return clonedElement;

    const card =
      dragElement.tagName.toLowerCase() === "stzh-card" ? dragElement : dragElement.querySelector("stzh-card");

    if (card) {
      const originalElement = dragElement as HTMLStzhCardElement;
      const clonedElement = dragElement.cloneNode(true) as HTMLStzhCardElement;

      // copy every property that is not reflected to its attribute
      clonedElement.heading = originalElement.heading;
      clonedElement.subtitle = originalElement.subtitle;
      clonedElement.headerActions = originalElement.headerActions;
      clonedElement.footerActions = originalElement.footerActions;
      clonedElement.hideMovementActions = originalElement.hideMovementActions;

      return clonedElement;
    }
  };

  async componentWillLoad() {
    this.isStzhAnchornavVisible =
      document.documentElement.style.getPropertyValue("--stzh-anchornav-is-visible") === "1";
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "cardlist");
    }
  }

  componentDidLoad() {
    this.isStzhAnchornavVisible =
      document.documentElement.style.getPropertyValue("--stzh-anchornav-is-visible") === "1";
    this.updateCollapsedState();
  }

  render() {
    const classes = {
      "stzh-cardlist": true,
      "stzh-cardlist--has-toggle-link": this.showToggleLink,
      "stzh-cardlist--max-3-cols": this.isStzhAnchornavVisible || this.max3Columns,
      [`stzh-cardlist--layout-${this.layout}`]: !!this.layout,
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-cardlist__header">
            <div class="stzh-cardlist__description">
              {this.description ? this.description : <slot name="description"></slot>}
            </div>
            {this.showToggleLink && (
              <stzh-link
                class="stzh-cardlist__switch"
                icon={this.allCollapsed ? "list-expanded" : "list-shrinked"}
                label={this.allCollapsed ? this.localization.expandAll : this.localization.collapseAll}
                onClick={this.collapseClick}
              ></stzh-link>
            )}
          </div>

          <stzh-sortable
            disabled={!this.sortableEnabled}
            dataIdAttribute={this.sortableDataIdAttribute}
            ghostClone={this.sortableGhostClone}
          >
            <div class="stzh-cardlist__cards" role="list">
              <slot></slot>
            </div>
          </stzh-sortable>

          <div class="stzh-cardlist__footer">
            <stzh-container>
              <slot name="footer"></slot>
            </stzh-container>
          </div>
        </div>
      </Host>
    );
  }
}
